import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ScrollToTopButton from '../components/ScrollToTopButton';

const Portfolio = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    fetch('/portfolioProjects.json')
      .then(res => res.json())
      .then(data => setProjects(data))
      .catch(() => setProjects([]));
  }, []);

  const CollageLayout = ({ project, index }) => {
    const isEven = index % 2 === 0;
    
    return (
      <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-4 md:gap-8 lg:gap-16 items-center mb-16 md:mb-24 lg:mb-32`}>
        {/* Creative Collage Section */}
        <div className="w-full lg:w-1/2">
          <div
            className="relative"
            onMouseEnter={() => setHoveredProject(project.id)}
            onMouseLeave={() => setHoveredProject(null)}
          >
            {/* Main collage container */}
            <div className="relative h-[350px] md:h-[500px] lg:h-[600px] overflow-hidden rounded-2xl md:rounded-3xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 p-2 md:p-4">
              
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className={`absolute inset-0 bg-gradient-to-br ${project.color || 'from-blue-500/20 to-purple-500/20'}`} />
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]" />
              </div>

              {/* Creative image arrangement */}
              <div className="relative h-full">
                {/* Large featured image */}
                <div className="absolute top-8 left-4 w-[50%] h-[35%] rounded-2xl overflow-hidden shadow-2xl z-20 hover:scale-105 transition-transform duration-300">
                  <img
                    src={project.images?.[0] || '/api/placeholder/400/300'}
                    alt={`${project.title} - Main view`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                {/* Medium image - top right */}
                <div className="absolute top-4 right-4 w-[48%] h-[35%] rounded-2xl overflow-hidden shadow-xl z-15 hover:scale-105 transition-transform duration-300">
                  <img
                    src={project.images?.[1] || '/api/placeholder/350/250'}
                    alt={`${project.title} - Feature view`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Tall mobile mockup */}
                <div className="absolute bottom-4 right-4 w-[25%] h-[55%] rounded-2xl overflow-hidden shadow-xl z-25 hover:scale-105 transition-transform duration-300">
                  <img
                    src={project.images?.[2] || '/api/placeholder/300/400'}
                    alt={`${project.title} - Mobile view`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Bottom left medium */}
                <div className="absolute bottom-4 left-4 w-[75%] h-[45%] rounded-2xl overflow-hidden shadow-xl z-10 hover:scale-105 transition-transform duration-300">
                  <img
                    src={project.images?.[3] || '/api/placeholder/450/280'}
                    alt={`${project.title} - Dashboard`}
                    className="w-full h-full object-fit"
                  />
                </div>

                {/* Small floating images */}
                <div className="absolute top-[25%] left-[40%] w-[20%] h-[45%] rounded-xl overflow-hidden shadow-lg z-30 hover:scale-110 transition-transform duration-300">
                  <img
                    src={project.images?.[4] || '/api/placeholder/320/240'}
                    alt={`${project.title} - Feature`}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="absolute top-[23%] right-[65%] w-[18%] h-[35%] rounded-xl overflow-hidden shadow-lg z-20 hover:scale-110 transition-transform duration-300">
                  <img
                    src={project.images?.[5] || '/api/placeholder/380/290'}
                    alt={`${project.title} - Detail`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Floating elements and decorations */}
              {/* <div className="absolute top-8 right-8 w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-20 animate-pulse" /> */}
              <div className="absolute bottom-8 left-8 w-8 h-8 bg-gradient-to-br from-green-400 to-blue-500 rounded-full opacity-30" />
              
              {/* Tech stack indicators */}
              {/* <div className="absolute bottom-6 right-6 flex gap-2">
                {project.tech?.slice(0, 3).map((tech, i) => (
                  <div key={tech} className="px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                    {tech}
                  </div>
                ))}
              </div> */}

              {/* Project number badge */}
              <div 
                className="absolute top-6 left-6 w-12 h-12 backdrop-blur-sm rounded-full flex items-center justify-center z-30"
                style={{
                  background: project.badgeColor 
                    ? `linear-gradient(to bottom right, ${project.badgeColor}CC, ${project.badgeColor}99)` 
                    : 'linear-gradient(to bottom right, rgba(0,0,0,0.8), rgba(0,0,0,0.6))'
                }}
              >
                <span className="text-white font-bold text-lg">{project.id}</span>
              </div>
            </div>

            {/* Simple hover effect */}
            <div 
              className={`absolute inset-0 rounded-3xl ring-4 ring-blue-400/30 ring-offset-2 ring-offset-transparent pointer-events-none transition-opacity duration-300 ${
                hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
              }`}
            />
          </div>
        </div>

        {/* Content Section */}
        <div className="w-full lg:w-1/2 px-2 md:px-4 lg:px-0">
          <div className="space-y-4 md:space-y-6 lg:space-y-8">
            {/* Project Title */}
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3 lg:mb-4">
                {project.title}
              </h2>
              <p className="text-sm md:text-base lg:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Tech Stack */}
            <div>
              <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-3">
                Technologies Used
              </h3>
              <div className="flex flex-wrap gap-1 md:gap-2">
                {project.tech?.map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="px-2 py-1 md:px-3 md:py-1 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs md:text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Key Features/Bullet Points */}
            {project.features && project.features.length > 0 && (
              <div>
                <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2 md:mb-3">
                  Key Features
                </h3>
                <ul className="space-y-1 md:space-y-2">
                  {project.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2 md:gap-3">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mt-1.5 md:mt-2 flex-shrink-0" />
                      <span className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-300 leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* Visit Website Link */}
                {project.demo && (
                  <div className="mt-4 md:mt-5">
                    <a
                      href={project.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 text-xs md:text-sm group"
                    >
                      <svg className="w-3 h-3 md:w-4 md:h-4 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Visit Live Website
                      <div className="w-0 h-0.5 bg-white/30 group-hover:w-full transition-all duration-300 absolute bottom-1 left-4"></div>
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 lg:gap-6 justify-center items-center px-4">
              {/* {project.demo && (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2.5 md:px-6 md:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm md:text-base"
                >
                  <span>View Live Demo</span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )} */}
              
              {/* {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-4 py-2.5 md:px-6 md:py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-full hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 hover:scale-105 text-sm md:text-base"
                >
                  <span>View Code</span>
                  <svg className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </a>
              )} */}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-blue-900/10 dark:to-purple-900/10">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="relative z-10 text-center pt-16 md:pt-20 lg:pt-24 pb-8 md:pb-12 lg:pb-16 px-4 md:px-6 lg:px-8">
          {/* Floating Badge */}
          <div className="inline-block mb-4 md:mb-6 lg:mb-8 animate-bounce">
            <div className="inline-block transform -rotate-3 hover:rotate-0 transition-transform duration-300">
              <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-1.5 md:px-6 md:py-3 lg:px-8 lg:py-4 rounded-2xl md:rounded-3xl font-bold text-xs md:text-base lg:text-lg">
                ✨ Top 3 of Many Masterpieces ✨
              </div>
            </div>
          </div>

          <h1 className="text-2xl md:text-5xl lg:text-7xl font-bold mb-3 md:mb-6 lg:mb-8">
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Portfolio
            </span>
            <br />
            <span className="text-gray-900 dark:text-white">Showcase</span>
          </h1>

          <p className="text-sm md:text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-6 md:mb-10 lg:mb-12 max-w-4xl mx-auto leading-relaxed px-2">
          A curated collection of websites developed with a focus on design, function, and user experience.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 lg:gap-6 justify-center items-center px-4">
            <button 
              onClick={() => navigate('/contact')}
              className="w-full sm:w-auto px-5 py-2.5 md:px-8 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-xs md:text-base"
            >
              <span>Let's Connect</span>
              <svg className="w-3 h-3 md:w-5 md:h-5 inline ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
            
            {/* <button className="w-full sm:w-auto px-5 py-2.5 md:px-8 md:py-4 border-2 border-blue-600/30 text-blue-600 dark:text-blue-400 font-semibold rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300 text-xs md:text-base">
              Explore All Work
            </button> */}
          </div>
        </div>

        {/* Projects Section */}
        <div className="max-w-7xl mx-auto">
          {projects.map((project, index) => (
            <CollageLayout key={project.id} project={project} index={index} />
          ))}
        </div>

        {/* Innovative CTA Section */}
        <div className="relative mt-8 md:mt-16 lg:mt-24 xl:mt-32 py-8 md:py-16 lg:py-20 xl:py-24 overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-blue-500/10 to-purple-500/10 rounded-2xl md:rounded-3xl">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.1),transparent_50%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.1),transparent_50%)]" />
            <div className="absolute top-0 right-0 w-20 h-20 md:w-32 md:h-32 bg-gradient-to-br from-green-400/20 to-blue-400/20 rounded-full blur-xl animate-pulse" />
            <div className="absolute bottom-0 left-0 w-24 h-24 md:w-40 md:h-40 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-xl animate-pulse delay-1000" />
          </div>

          <div className="relative z-10 text-center px-3 md:px-4 lg:px-8">
            {/* Floating Icons */}
            <div className="flex justify-center mb-4 md:mb-8">
              <div className="relative">
                {/* Money/Budget Icon */}
                <div className="absolute -left-12 -top-3 md:-left-16 md:-top-4 w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg animate-bounce">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>

                {/* Main Icon */}
                <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl md:rounded-3xl flex items-center justify-center mx-auto shadow-xl">
                  <svg className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>

                {/* Growth Icon */}
                <div className="absolute -right-12 -top-3 md:-right-16 md:-top-4 w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl md:rounded-2xl flex items-center justify-center shadow-lg animate-bounce delay-500">
                  <svg className="w-4 h-4 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Main Heading */}
            <h2 className="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-bold mb-3 md:mb-6 lg:mb-8">
              <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
                Got a Budget?
              </span>
              <br />
              <span className="text-gray-800 dark:text-white">
                Let's Make Magic! ✨
              </span>
            </h2>

            {/* Subheading */}
            <p className="text-sm md:text-lg lg:text-xl xl:text-2xl text-gray-600 dark:text-gray-300 mb-6 md:mb-8 lg:mb-12 max-w-4xl mx-auto leading-relaxed">
              Whether you have <span className="font-semibold text-green-600">₹5,000 or ₹50,000</span>, we'll craft a digital solution that fits your vision and delivers results that matter.
            </p>

            {/* Value Propositions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-8 md:mb-12 lg:mb-16 max-w-5xl mx-auto">
              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-green-200/30 hover:scale-105 transition-all duration-300">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-gray-800 dark:text-white">Budget-Friendly</h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Transparent pricing that works with your budget, no hidden surprises</p>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-blue-200/30 hover:scale-105 transition-all duration-300">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-gray-800 dark:text-white">More Clients</h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Websites that convert visitors into customers and grow your business</p>
              </div>

              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-purple-200/30 hover:scale-105 transition-all duration-300">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <svg className="w-5 h-5 md:w-6 md:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-bold text-base md:text-lg mb-1 md:mb-2 text-gray-800 dark:text-white">Loved by Users</h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs md:text-sm">Beautiful, fast, and user-friendly designs that people actually enjoy using</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 lg:gap-6 justify-center items-center">
              {/* WhatsApp CTA */}
              <button 
                onClick={() => window.open('https://wa.me/918056064224?text=Hi! I have a budget and I\'m interested in discussing a project with Tech4All. Let\'s talk!', '_blank')}
                className="group w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 lg:px-10 lg:py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 text-sm md:text-base lg:text-lg relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center justify-center">
                  <svg className="w-4 h-4 md:w-6 md:h-6 mr-2 md:mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <span>💰 I Have a Budget!</span>
                </div>
              </button>

              {/* Alternative CTA */}
              <button 
                onClick={() => window.open('https://wa.me/918056064224?text=Hi Tech4All! I want to grow my business and get more clients. Can we discuss how you can help?', '_blank')}
                className="group w-full sm:w-auto px-6 py-3 md:px-8 md:py-4 lg:px-10 lg:py-5 border-2 border-blue-500/50 text-blue-600 dark:text-blue-400 font-bold rounded-full hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300 hover:scale-105 text-sm md:text-base lg:text-lg relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative flex items-center justify-center">
                  <svg className="w-4 h-4 md:w-6 md:h-6 mr-2 md:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span>🚀 I Want More Clients!</span>
                </div>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 md:mt-12 lg:mt-16 flex flex-wrap justify-center items-center gap-4 md:gap-8 text-xs md:text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Free Consultation</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>24/7 Support</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-1 md:mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Affordable Hosting Charge</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ScrollToTopButton />
    </div>
  );
};

export default Portfolio;